// src/pages/finance-portal/journal-approvals/JournalApprovals.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Switch, Tooltip, Tabs, Tab,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

const CONDITION_TYPES = [
    { value: 'all', label: 'All Entries' },
    { value: 'amount_gt', label: 'Amount Greater Than' },
];

const emptyRule = { condition_type: 'all', condition_value: '', approver_user_id: '', sequence: 1, is_active: true };

export default function JournalApprovals() {
    const [tabValue, setTabValue] = useState(0);

    // Pending approvals
    const [pendingEntries, setPendingEntries] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [pendingError, setPendingError] = useState(null);

    // Approval rules
    const [rules, setRules] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(true);
    const [rulesError, setRulesError] = useState(null);

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Approve/Reject dialog
    const [actionDialog, setActionDialog] = useState({ open: false, type: null, entry: null });
    const [actionComment, setActionComment] = useState('');
    const [actionSaving, setActionSaving] = useState(false);

    // Rule dialog
    const [ruleDialog, setRuleDialog] = useState({ open: false, editing: null });
    const [ruleForm, setRuleForm] = useState(emptyRule);
    const [ruleSaving, setRuleSaving] = useState(false);

    // Delete rule
    const [deleteDialog, setDeleteDialog] = useState({ open: false, rule: null });
    const [deleting, setDeleting] = useState(false);

    const fetchPending = useCallback(async () => {
        setPendingLoading(true);
        setPendingError(null);
        try {
            const res = await axiosInstance.get('/api/finance/journal-approvals/pending');
            if (res.data.success) {
                setPendingEntries(res.data.data);
            }
        } catch (err) {
            setPendingError(err.response?.data?.message || 'Failed to load pending approvals');
        } finally {
            setPendingLoading(false);
        }
    }, []);

    const fetchRules = useCallback(async () => {
        setRulesLoading(true);
        setRulesError(null);
        try {
            const res = await axiosInstance.get('/api/finance/journal-approval-rules');
            if (res.data.success) {
                setRules(res.data.data);
            }
        } catch (err) {
            setRulesError(err.response?.data?.message || 'Failed to load approval rules');
        } finally {
            setRulesLoading(false);
        }
    }, []);

    useEffect(() => { fetchPending(); fetchRules(); }, [fetchPending, fetchRules]);

    // Approve / Reject
    const openAction = (type, entry) => {
        setActionDialog({ open: true, type, entry });
        setActionComment('');
    };

    const submitAction = async () => {
        setActionSaving(true);
        try {
            const entryId = actionDialog.entry.id;
            if (actionDialog.type === 'approve') {
                await axiosInstance.post(`/api/finance/journal-entries/${entryId}/approve`, { comment: actionComment });
                setSuccess('Entry approved');
            } else {
                await axiosInstance.post(`/api/finance/journal-entries/${entryId}/reject`, { comment: actionComment });
                setSuccess('Entry rejected');
            }
            setActionDialog({ open: false, type: null, entry: null });
            fetchPending();
        } catch (err) {
            setError(err.response?.data?.message || `Failed to ${actionDialog.type} entry`);
        } finally {
            setActionSaving(false);
        }
    };

    // Rule CRUD
    const openRuleDialog = (rule = null) => {
        if (rule) {
            setRuleForm({
                condition_type: rule.condition_type,
                condition_value: rule.condition_value || '',
                approver_user_id: rule.approver_user_id || '',
                sequence: rule.sequence || 1,
                is_active: rule.is_active,
            });
        } else {
            setRuleForm(emptyRule);
        }
        setRuleDialog({ open: true, editing: rule });
    };

    const saveRule = async () => {
        setRuleSaving(true);
        const payload = {
            ...ruleForm,
            condition_value: ruleForm.condition_type === 'all' ? null : ruleForm.condition_value,
        };
        try {
            if (ruleDialog.editing) {
                await axiosInstance.put(`/api/finance/journal-approval-rules/${ruleDialog.editing.id}`, payload);
                setSuccess('Rule updated');
            } else {
                await axiosInstance.post('/api/finance/journal-approval-rules', payload);
                setSuccess('Rule created');
            }
            setRuleDialog({ open: false, editing: null });
            fetchRules();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save rule');
        } finally {
            setRuleSaving(false);
        }
    };

    const handleDeleteRule = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/journal-approval-rules/${deleteDialog.rule.id}`);
            setSuccess('Rule deleted');
            setDeleteDialog({ open: false, rule: null });
            fetchRules();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete rule');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FactCheckIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Journal Approvals</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Approval workflow management</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Tabs */}
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem', minHeight: 40 },
                    '& .Mui-selected': { color: `${ACCENT} !important` },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label={`Pending Approvals${pendingEntries.length > 0 ? ` (${pendingEntries.length})` : ''}`} />
                    <Tab label="Approval Rules" />
                </Tabs>

                {/* Tab 0: Pending Approvals */}
                {tabValue === 0 && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Entry #', 'Date', 'Description', 'Total Debit', 'Actions'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingLoading ? (
                                        [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={5}><Skeleton height={36} /></TableCell></TableRow>)
                                    ) : pendingError ? (
                                        <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                            <Alert severity="error" sx={{ fontFamily }}>{pendingError}</Alert>
                                        </TableCell></TableRow>
                                    ) : pendingEntries.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                            <CheckCircleIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No entries pending approval</Typography>
                                        </TableCell></TableRow>
                                    ) : pendingEntries.map(entry => (
                                        <TableRow key={entry.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>{entry.entry_number}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{entry.entry_date}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.description || '—'}</TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(entry.total_debit)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                                                        onClick={() => openAction('approve', entry)}
                                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, fontSize: '0.75rem', py: 0.5 }}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button size="small" variant="outlined" startIcon={<CancelIcon />}
                                                        onClick={() => openAction('reject', entry)}
                                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px', borderColor: '#ef4444', color: '#ef4444', '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' }, fontSize: '0.75rem', py: 0.5 }}
                                                    >
                                                        Reject
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}

                {/* Tab 1: Approval Rules */}
                {tabValue === 1 && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openRuleDialog()}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                Add Rule
                            </Button>
                        </Box>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            {['Condition Type', 'Condition Value', 'Approver', 'Sequence', 'Active', 'Actions'].map(h => (
                                                <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rulesLoading ? (
                                            [1, 2, 3].map(i => <TableRow key={i}><TableCell colSpan={6}><Skeleton height={36} /></TableCell></TableRow>)
                                        ) : rulesError ? (
                                            <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                <Alert severity="error" sx={{ fontFamily }}>{rulesError}</Alert>
                                            </TableCell></TableRow>
                                        ) : rules.length === 0 ? (
                                            <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No approval rules configured</Typography>
                                            </TableCell></TableRow>
                                        ) : rules.map(rule => (
                                            <TableRow key={rule.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                <TableCell>
                                                    <Chip label={CONDITION_TYPES.find(c => c.value === rule.condition_type)?.label || rule.condition_type}
                                                        size="small" sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, height: 24, bgcolor: `${ACCENT}15`, color: ACCENT }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                    {rule.condition_type === 'amount_gt' ? formatCurrency(rule.condition_value) : '—'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                    {rule.approver_name || rule.approver_email || `User #${rule.approver_user_id}`}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{rule.sequence}</TableCell>
                                                <TableCell>
                                                    <Chip label={rule.is_active ? 'Active' : 'Inactive'} size="small"
                                                        sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: rule.is_active ? '#dcfce7' : '#f1f5f9', color: rule.is_active ? '#166534' : '#64748b' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openRuleDialog(rule)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                        <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog({ open: true, rule })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </>
                )}
            </Box>

            {/* Approve/Reject Dialog */}
            <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: null, entry: null })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {actionDialog.type === 'approve' ? 'Approve Entry' : 'Reject Entry'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    {actionDialog.entry && (
                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                <strong>Entry:</strong> {actionDialog.entry.entry_number}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                <strong>Description:</strong> {actionDialog.entry.description || '—'}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                <strong>Total:</strong> {formatCurrency(actionDialog.entry.total_debit)}
                            </Typography>
                        </Box>
                    )}
                    <TextField label="Comment (optional)" value={actionComment}
                        onChange={e => setActionComment(e.target.value)}
                        fullWidth size="small" multiline rows={3}
                        placeholder={actionDialog.type === 'reject' ? 'Reason for rejection...' : 'Optional approval note...'}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setActionDialog({ open: false, type: null, entry: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitAction} disabled={actionSaving}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: actionDialog.type === 'approve' ? ACCENT : '#ef4444',
                            '&:hover': { bgcolor: actionDialog.type === 'approve' ? '#16a34a' : '#dc2626' },
                        }}
                    >
                        {actionSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : actionDialog.type === 'approve' ? 'Approve' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rule Dialog */}
            <Dialog open={ruleDialog.open} onClose={() => setRuleDialog({ open: false, editing: null })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {ruleDialog.editing ? 'Edit Approval Rule' : 'Add Approval Rule'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontFamily }}>Condition Type</InputLabel>
                        <Select value={ruleForm.condition_type} onChange={e => setRuleForm(prev => ({ ...prev, condition_type: e.target.value }))}
                            label="Condition Type" sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            {CONDITION_TYPES.map(ct => (
                                <MenuItem key={ct.value} value={ct.value} sx={{ fontFamily }}>{ct.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {ruleForm.condition_type === 'amount_gt' && (
                        <TextField label="Amount Threshold" value={ruleForm.condition_value}
                            onChange={e => setRuleForm(prev => ({ ...prev, condition_value: e.target.value }))}
                            fullWidth size="small" type="number"
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' }, startAdornment: <Typography sx={{ fontFamily, mr: 0.5, color: '#64748b' }}>$</Typography> }}
                            InputLabelProps={{ sx: { fontFamily } }}
                        />
                    )}
                    <TextField label="Approver User ID" value={ruleForm.approver_user_id}
                        onChange={e => setRuleForm(prev => ({ ...prev, approver_user_id: e.target.value }))}
                        fullWidth size="small" type="number"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Sequence" value={ruleForm.sequence}
                        onChange={e => setRuleForm(prev => ({ ...prev, sequence: parseInt(e.target.value) || 1 }))}
                        fullWidth size="small" type="number"
                        helperText="Lower numbers are evaluated first"
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' }, inputProps: { min: 1 } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.75rem' } }}
                    />
                    <FormControlLabel
                        control={<Switch checked={ruleForm.is_active} onChange={e => setRuleForm(prev => ({ ...prev, is_active: e.target.checked }))}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                        />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Active</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setRuleDialog({ open: false, editing: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveRule}
                        disabled={ruleSaving || !ruleForm.approver_user_id || (ruleForm.condition_type === 'amount_gt' && !ruleForm.condition_value)}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {ruleSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : ruleDialog.editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Rule Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, rule: null })}
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>
                        Are you sure you want to delete this approval rule? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, rule: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDeleteRule} disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
